import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

// Comparte una cotización con otro compañero (lectura y edición). Lista los
// colaboradores actuales con opción de revocar el acceso.
export function CotShareModal({ open, onOpenChange, cotId, ownerEmail, sharedWith = [], onChange }) {
  const [agents, setAgents] = useState([]);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.get("/cotizaciones/share-targets")
      .then(({ data }) => { if (alive) setAgents(data.items || []); })
      .catch(() => { if (alive) toast.error("No se pudo cargar la lista de compañeros"); });
    return () => { alive = false; };
  }, [open]);

  const available = agents.filter((a) => a.email !== ownerEmail && !(sharedWith || []).includes(a.email));
  const nameOf = (email) => agents.find((a) => a.email === email)?.name || email;

  const share = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cotizaciones/${cotId}/share`, { email: picked });
      onChange?.(data.shared_with || []);
      toast.success(`Compartida con ${nameOf(picked)}`);
      setPicked("");
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const unshare = async (email) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/cotizaciones/${cotId}/share/${encodeURIComponent(email)}`);
      onChange?.(data.shared_with || []);
      toast.success(`Acceso revocado a ${nameOf(email)}`);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cot-share-with-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Compartir con un compañero</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">El compañero podrá <strong>ver y editar</strong> esta cotización. Aparecerá en su lista de cotizaciones.</p>

        <div className="flex items-center gap-2">
          <Select value={picked} onValueChange={setPicked}>
            <SelectTrigger className="flex-1" data-testid="cot-share-select"><SelectValue placeholder="Elige un compañero…" /></SelectTrigger>
            <SelectContent>
              {available.length === 0
                ? <div className="px-2 py-1.5 text-sm text-muted-foreground">No hay más compañeros disponibles</div>
                : available.map((a) => <SelectItem key={a.email} value={a.email}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={share} disabled={!picked || busy} data-testid="cot-share-add">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="mr-1.5 h-4 w-4" />}Compartir
          </Button>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Colaboradores</p>
          {(sharedWith || []).length === 0 ? (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground" data-testid="cot-share-empty">Aún no la has compartido con nadie.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {sharedWith.map((email) => (
                <li key={email} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`cot-share-row-${email}`}>
                  <span>{nameOf(email)} <span className="text-xs text-muted-foreground">· {email}</span></span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => unshare(email)} disabled={busy} title="Revocar acceso" data-testid={`cot-share-remove-${email}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
